import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  Brain,
  CheckCircle2,
  ClipboardCheck,
  CloudOff,
  History as HistoryIcon,
  Info,
  Loader2,
  Maximize2,
  Mic,
  Minimize2,
  Pause,
  Play,
  RefreshCcw,
  Square,
  Timer,
  TriangleAlert,
  Trash2,
  X,
} from "lucide-react";
import { Button } from "@akuna/ui";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription, AlertTitle } from "@akuna/ui";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@akuna/ui";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import type { Database } from "@/integrations/supabase/types";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { type DiagnosisSuggestion, type UnknownTerm } from "@/hooks/useConsultationTranscription";
import { useTranscription } from "@/contexts/TranscriptionContext";
import {
  formatTimestamp,
  formatDuration,
  formatAutoSaveTime,
  DRAWER_HIDE_DELAY,
} from "./transcription";
import { isInvalidSonioxApiKeyError } from "./transcription/capture-error-display";
import { buildTranscriptionViewModel } from "./transcription/transcription-view-model";
import { useTranslation } from "react-i18next";
type ConsultationTranscriptionRow = Database["public"]["Tables"]["consultation_transcriptions"]["Row"];

/**
 * Main transcription dialog component
 * Handles real-time transcription, AI processing, and medical record integration
 */
export const ConsultationTranscriptionDialog = () => {
  const { t } = useTranslation("medicalRecords");
  const {
    state,
    isOpen,
    isMinimized,
    session,
    clinicId,
    petName,
    appointmentDate,
    summary,
    warning,
    error,
    structured,
    diagnosisSuggestions,
    unknownTerms,
    languageWarning,
    isProcessing,
    isStarting,
    isFinalizing,
    isTimerRunning,
    recordingPhase,
    recordingDuration,
    editableTranscript,
    sonioxError,
    sessionError,
    isSessionLoading,
    syncStatus,
    lastSyncedAt,
    isInterrupted,
    showReconnectBanner,
    isReconnecting,
    reconnect,
    finalizeFailed,
    retryFinalize,
    history,
    isHistoryLoading,
    selectedHistoryId,
    isCancelRecordingDialogOpen,
    canClearTranscription,
    minimize,
    maximize,
    closeDialog,
    clearSession,
    discardSession,
    setIsCancelRecordingDialogOpen,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    retryAiExtraction,
    refreshHistory,
    applyHistoryEntry,
    reviewHistoryEntry,
    onMergeFields,
    onApply,
  } = useTranscription();

  // UI state
  const [isDrawerRendered, setIsDrawerRendered] = useState(false);
  const [isDrawerAnimatedIn, setIsDrawerAnimatedIn] = useState(false);

  // Refs
  const hideDrawerTimeoutRef = useRef<number | null>(null);
  const showDrawerRafRef = useRef<number | null>(null);
  const renderedSessionSnapshotRef = useRef(session);

  if (session) {
    renderedSessionSnapshotRef.current = session;
  }

  const renderedSession = session ?? renderedSessionSnapshotRef.current;
  const renderedClinicId = renderedSession?.clinicId ?? clinicId;
  const renderedPetId = renderedSession?.petId ?? session?.petId ?? "";
  const renderedPetName = renderedSession?.petName ?? petName;
  const renderedAppointmentDate = renderedSession?.appointmentDate ?? appointmentDate;

  // Computed values
  const hasActiveRecording = recordingPhase === "recording" || recordingPhase === "paused";
  const isBusyProcessing = state === "finalizing" || state === "analyzing";
  const appointmentLabel = useMemo(() => formatTimestamp(renderedAppointmentDate), [renderedAppointmentDate]);
  const lastSyncedLabel = useMemo(() => formatAutoSaveTime(lastSyncedAt), [lastSyncedAt]);
  const historyEmpty = history.length === 0;
  const canTriggerClearAction =
    !isBusyProcessing && (state === "ready" || state === "analysis_failed") && canClearTranscription;


  const captureErrorAlert = useMemo(() => {
    if (!sonioxError) return null;
    // Em "interrupted" o painel de conexao perdida ja explica a situacao em linguagem
    // de usuario; repetir o erro cru do SDK ("WebSocket error occurred") so assusta.
    if (state === "interrupted") return null;
    if (isInvalidSonioxApiKeyError(sonioxError)) {
      return {
        title: t("transcription.errors.invalidApiKeyTitle"),
        description: t("transcription.errors.invalidApiKeyDescription"),
      };
    }
    return {
      title: t("transcription.captureErrorTitle"),
      description: sonioxError,
    };
  }, [sonioxError, state, t]);

  // Status label
  const statusLabel = useMemo(() => {
    if (isStarting) return t("transcription.status.preparingMicrophone");
    if (state === "interrupted") return t("transcription.status.interrupted");
    if (recordingPhase === "recording") return t("transcription.status.liveTranscribing");
    if (recordingPhase === "paused") return t("transcription.status.paused");
    if (state === "finalizing") return t("transcription.status.processingConsultation");
    if (state === "analyzing") return t("transcription.status.generatingStructure");
    if (state === "ready" || state === "analysis_failed") return t("transcription.status.completed");
    return t("transcription.status.ready");
  }, [isStarting, recordingPhase, state, t]);

  // Drawer render lifecycle
  useEffect(() => {
    const shouldRender = isOpen && !isMinimized && !!session;
    if (shouldRender) {
      if (hideDrawerTimeoutRef.current) {
        window.clearTimeout(hideDrawerTimeoutRef.current);
        hideDrawerTimeoutRef.current = null;
      }
      setIsDrawerRendered(true);
      // setTimeout(0) ensures the browser paints the mounted off-screen position
      // before we flip isDrawerAnimatedIn, triggering the slide-in transition.
      showDrawerRafRef.current = window.setTimeout(() => {
        setIsDrawerAnimatedIn(true);
        showDrawerRafRef.current = null;
      }, 0) as unknown as number;
      return;
    }

    if (showDrawerRafRef.current) {
      window.clearTimeout(showDrawerRafRef.current);
      showDrawerRafRef.current = null;
    }
    setIsDrawerAnimatedIn(false);

    if (!isDrawerRendered) {
      return;
    }

    hideDrawerTimeoutRef.current = window.setTimeout(() => {
      setIsDrawerRendered(false);
      hideDrawerTimeoutRef.current = null;
    }, DRAWER_HIDE_DELAY);

    return () => {
      if (hideDrawerTimeoutRef.current) {
        window.clearTimeout(hideDrawerTimeoutRef.current);
        hideDrawerTimeoutRef.current = null;
      }
    };
  }, [isDrawerRendered, isMinimized, isOpen, session]);

  // Event handlers
  const handleBackdropClick = () => {
    if (hasActiveRecording) {
      minimize();
      return;
    }

    if (diagnosisSuggestions.length > 0) {
      minimize();
      return;
    }
    discardSession();
  };

  const handleClearClick = () => {
    clearSession();
    toast.success(t("transcription.toasts.cleared"));
  };

  const handleApply = () => {
    if ((!structured || Object.keys(structured).length === 0) && !summary?.trim()) {
      toast.error(t("transcription.toasts.noStructuredData"));
      return;
    }

    if (!onApply) {
      toast.error(t("transcription.toasts.noLinkedForm"));
      return;
    }

    onApply?.(structured ?? {}, editableTranscript, summary ?? null);
  };

  const handleSuggestionApply = (suggestion: DiagnosisSuggestion) => {
    if (!suggestion?.label) return;
    const diagnosisText = suggestion.label;
    const treatmentText = suggestion.recommended_tests
      ? t("transcription.recommendedTests", { tests: suggestion.recommended_tests })
      : undefined;

    onMergeFields?.({
      presumptive_diagnosis: diagnosisText,
      ...(treatmentText && { treatment: treatmentText }),
    });
    toast.success(t("transcription.toasts.suggestionApplied"));
  };

  const handleUnknownTermNote = (term: UnknownTerm) => {
    toast.info(term.term, {
      description: term.context ?? t("transcription.toasts.reviewTerm"),
    });
  };

  const transcriptionView = buildTranscriptionViewModel({
    isOpen,
    isMinimized,
    hasSession: Boolean(session),
    recordingPhase,
    isBusy: isBusyProcessing || state === "analysis_failed",
  });
  const drawerVisible = transcriptionView.showSheet && isDrawerRendered;
  const floatingVisible = transcriptionView.showMiniBar;

  if (!isDrawerRendered && !floatingVisible) {
    return null;
  }

  return (
    <>
      {/* Main Drawer */}
      {isDrawerRendered && (
        <div
          className={cn(
            "fixed inset-0 z-50 flex items-end md:items-stretch md:justify-end",
            drawerVisible ? "pointer-events-auto" : "pointer-events-none",
          )}
        >
          <div
            className={cn(
              "absolute inset-0 bg-background/60 backdrop-blur-sm transition-opacity duration-300",
              drawerVisible ? "opacity-100" : "opacity-0 pointer-events-none",
            )}
            onClick={handleBackdropClick}
          />
          <aside
            className={cn(
              "relative flex h-[94dvh] w-full flex-col rounded-t-3xl border-t bg-background-app shadow-2xl transition-transform duration-300 ease-in-out motion-reduce:transition-none md:h-full md:max-w-[560px] md:rounded-none md:border-l md:border-t-0 md:bg-background",
              isDrawerAnimatedIn
                ? "translate-y-0 md:translate-x-0"
                : "translate-y-full md:translate-x-full md:translate-y-0",
            )}
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b px-4 py-4 md:px-6">
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t("transcription.title")}
                </p>
                <p className="truncate text-lg font-bold text-foreground">{renderedPetName ?? t("transcription.consultationFallback")}</p>
                <p className="text-xs text-muted-foreground">
                  {appointmentLabel ? t("transcription.scheduledFor", { date: appointmentLabel }) : t("transcription.noAppointmentLink")}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {syncStatus === "saving" ? (
                  <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    {t("transcription.sync.saving")}
                  </span>
                ) : syncStatus === "offline" ? (
                  <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                    <CloudOff className="h-3 w-3" />
                    {t("transcription.sync.offline")}
                  </span>
                ) : (
                  syncStatus === "saved" &&
                  lastSyncedLabel && (
                    <span className="text-xs text-muted-foreground">
                      {t("transcription.savedAt", { time: lastSyncedLabel })}
                    </span>
                  )
                )}
                <Button variant="ghost" size="icon" onClick={minimize} aria-label={t("transcription.minimize")}>
                  <Minimize2 className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={closeDialog} aria-label={t("transcription.close")}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Content */}
            <ScrollArea className="flex-1 px-4 py-4 md:px-6">
              <TooltipProvider>
                <div className="space-y-4">
                  {/* Alerts */}
                  {!renderedClinicId && drawerVisible && (
                    <Alert variant="danger">
                      <AlertCircle className="h-4 w-4" />
                      <AlertTitle>{t("transcription.clinicRequiredTitle")}</AlertTitle>
                      <AlertDescription>{t("transcription.clinicRequiredDescription")}</AlertDescription>
                    </Alert>
                  )}

                  {warning && (
                    <Alert className="border-yellow-500/60 bg-yellow-50 text-yellow-900 dark:bg-yellow-500/10 dark:text-yellow-100">
                      <TriangleAlert className="h-4 w-4 text-yellow-600 dark:text-yellow-200" />
                      <AlertTitle>{t("transcription.warningTitle")}</AlertTitle>
                      <AlertDescription>{warning}</AlertDescription>
                    </Alert>
                  )}

                  {languageWarning && (
                    <Alert variant="danger">
                      <TriangleAlert className="h-4 w-4" />
                      <AlertTitle>{t("transcription.unsupportedLanguageTitle")}</AlertTitle>
                      <AlertDescription>{languageWarning}</AlertDescription>
                    </Alert>
                  )}

                  {sessionError && (
                    <Alert variant="danger">
                      <AlertTitle>{t("transcription.sessionErrorTitle")}</AlertTitle>
                      <AlertDescription>{sessionError}</AlertDescription>
                    </Alert>
                  )}

                  {state === "interrupted" && showReconnectBanner && (
                    <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4">
                      <div className="flex gap-3">
                        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-destructive/10">
                          <CloudOff className="h-4 w-4 text-destructive" />
                        </span>
                        <div className="min-w-0 flex-1 space-y-1">
                          <p className="text-sm font-semibold text-foreground">
                            {t("transcription.connection.lostTitle")}
                          </p>
                          <p className="text-sm leading-snug text-muted-foreground">
                            {t("transcription.connection.lostDescription")}
                          </p>
                          <div className="flex flex-wrap items-center gap-2 pt-2">
                            <Button size="sm" variant="default" onClick={reconnect} disabled={isReconnecting}>
                              {isReconnecting ? (
                                <>
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  {t("transcription.connection.reconnecting")}
                                </>
                              ) : (
                                <>
                                  <RefreshCcw className="mr-2 h-4 w-4" />
                                  {t("transcription.connection.reconnect")}
                                </>
                              )}
                            </Button>
                            <Button size="sm" variant="ghost" onClick={stopRecording} disabled={isFinalizing}>
                              {t("transcription.connection.finishAnyway")}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {finalizeFailed && (
                    <Alert variant="danger">
                      <CloudOff className="h-4 w-4" />
                      <AlertTitle>{t("transcription.finalizeFailure.title")}</AlertTitle>
                      <AlertDescription className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <span>{t("transcription.finalizeFailure.description")}</span>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => void retryFinalize()}
                          disabled={isFinalizing}
                        >
                          {t("transcription.finalizeFailure.retry")}
                        </Button>
                      </AlertDescription>
                    </Alert>
                  )}

                  {state === "analysis_failed" && (
                    <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4">
                      <div className="flex gap-3">
                        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-500/10">
                          <TriangleAlert className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                        </span>
                        <div className="min-w-0 flex-1 space-y-1">
                          <p className="text-sm font-semibold text-foreground">
                            {t("transcription.aiFailure.title")}
                          </p>
                          <p className="text-sm leading-snug text-muted-foreground">
                            {t("transcription.aiFailure.description")}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Recording Controls */}
                  <div className="rounded-md border border-dashed p-4">
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm font-medium">{renderedPetName ? t("transcription.attendingPet", { pet: renderedPetName }) : t("transcription.attending")}</p>
                        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                          <span
                            className={cn(
                              "inline-flex h-2 w-2 shrink-0 rounded-full",
                              state === "interrupted"
                                ? "bg-destructive"
                                : recordingPhase === "recording"
                                  ? "bg-red-500 animate-pulse"
                                  : recordingPhase === "paused"
                                    ? "bg-amber-500"
                                    : isBusyProcessing
                                      ? "bg-primary"
                                      : "bg-muted",
                            )}
                          />
                          <span className="min-h-[1.25rem]">{statusLabel}</span>
                          {isTimerRunning && (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-foreground">
                              <Timer className="h-3 w-3" />
                              {formatDuration(recordingDuration)}
                            </span>
                          )}
                        </div>
                      </div>

                      {isBusyProcessing && recordingPhase === "idle" ? (
                        <div className="flex min-h-[48px] items-center gap-3 rounded-md border border-border/80 bg-muted/30 px-3 py-3">
                          <Loader2 className="h-5 w-5 shrink-0 animate-spin text-primary" aria-hidden />
                          <p className="text-sm font-medium leading-snug text-foreground">{statusLabel}</p>
                        </div>
                      ) : (
                        <div className="flex min-h-[40px] flex-wrap items-center gap-2">
                          {/* Interrompido: retomar a MESMA sessao, nunca "iniciar" (que zeraria o texto). */}
                          {state === "interrupted" && (
                            <>
                              <Button size="sm" variant="default" onClick={reconnect} disabled={isReconnecting}>
                                {isReconnecting ? (
                                  <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    {t("transcription.connection.reconnecting")}
                                  </>
                                ) : (
                                  <>
                                    <RefreshCcw className="mr-2 h-4 w-4" />
                                    {t("transcription.connection.reconnect")}
                                  </>
                                )}
                              </Button>
                              <Button size="sm" variant="destructive" onClick={stopRecording} disabled={isFinalizing}>
                                <Square className="mr-2 h-4 w-4" />
                                {t("transcription.finish")}
                              </Button>
                            </>
                          )}

                          {state !== "interrupted" && recordingPhase === "idle" && !isBusyProcessing && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={startRecording}
                              disabled={isStarting || isBusyProcessing || !renderedClinicId || isSessionLoading}
                            >
                              <Mic className="mr-2 h-4 w-4" />
                              {isStarting || isSessionLoading ? t("transcription.connecting") : t("transcription.startRecording")}
                            </Button>
                          )}

                          {state !== "interrupted" && recordingPhase === "recording" && (
                            <>
                              <Button size="sm" variant="outline" onClick={pauseRecording} disabled={isFinalizing}>
                                <Pause className="mr-2 h-4 w-4" />
                                {t("transcription.pause")}
                              </Button>
                              <Button size="sm" variant="destructive" onClick={stopRecording} disabled={isFinalizing}>
                                {isFinalizing ? (
                                  <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    {t("transcription.processing")}
                                  </>
                                ) : (
                                  <>
                                    <Square className="mr-2 h-4 w-4" />
                                    {t("transcription.stop")}
                                  </>
                                )}
                              </Button>
                            </>
                          )}

                          {state !== "interrupted" && recordingPhase === "paused" && (
                            <>
                              <Button
                                size="sm"
                                variant="default"
                                onClick={resumeRecording}
                                disabled={isFinalizing || isStarting}
                              >
                                <Play className="mr-2 h-4 w-4" />
                                {t("transcription.resume")}
                              </Button>
                              <Button size="sm" variant="destructive" onClick={stopRecording} disabled={isFinalizing}>
                                {isFinalizing ? (
                                  <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    {t("transcription.processing")}
                                  </>
                                ) : (
                                  <>
                                    <Square className="mr-2 h-4 w-4" />
                                    {t("transcription.finish")}
                                  </>
                                )}
                              </Button>
                            </>
                          )}

                          {state === "analysis_failed" && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => void retryAiExtraction()}
                              disabled={isProcessing}
                            >
                              {isProcessing ? (
                                <>
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  {t("transcription.aiFailure.retrying")}
                                </>
                              ) : (
                                <>
                                  <RefreshCcw className="mr-2 h-4 w-4" />
                                  {t("transcription.aiFailure.retry")}
                                </>
                              )}
                            </Button>
                          )}

                          {canTriggerClearAction && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-destructive hover:text-destructive"
                              onClick={handleClearClick}
                            >
                              {t("transcription.clear")}
                            </Button>
                          )}
                        </div>
                      )}

                      {captureErrorAlert && (
                        <Alert variant="danger">
                          <TriangleAlert className="h-4 w-4" />
                          <AlertTitle>{captureErrorAlert.title}</AlertTitle>
                          <AlertDescription>{captureErrorAlert.description}</AlertDescription>
                        </Alert>
                      )}
                    </div>
                  </div>

                  {/* Transcript */}
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-foreground" htmlFor="transcription-textarea">
                      {t("transcription.transcriptLabel")}
                    </label>
                    <Textarea
                      id="transcription-textarea"
                      value={editableTranscript}
                      readOnly
                      placeholder={t("transcription.transcriptPlaceholder")}
                      className="min-h-[180px] resize-y bg-muted/20"
                    />
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>
                        {editableTranscript.length > 0
                          ? `${editableTranscript.length} caracteres`
                          : t("transcription.noTranscript")}
                      </span>
                      {state === "ready" && (
                        <span className="inline-flex items-center gap-1 text-emerald-600">
                          <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                          {t("transcription.aiProcessed")}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Diagnosis Suggestions */}
                  {diagnosisSuggestions.length > 0 && (
                    <div className="rounded-md border border-primary/40 bg-primary/5 p-4">
                      <div className="flex items-center gap-2">
                        <Brain className="h-4 w-4 text-primary" />
                        <p className="text-sm font-semibold text-primary">{t("transcription.aiSuggestionsTitle")}</p>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-4 w-4 p-0">
                              <Info className="h-3 w-3 text-muted-foreground" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent className="border-transparent bg-foreground text-background max-w-[300px]">
                            <p>{t("transcription.aiSuggestionsDisclaimer")}</p>
                            <TooltipArrow className="fill-foreground" />
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      <div className="mt-3 space-y-3 text-sm">
                        {diagnosisSuggestions.map((suggestion) => (
                          <div key={suggestion.label} className="rounded-md border p-3">
                            <div className="flex items-center justify-between">
                              <p className="font-semibold">{suggestion.label}</p>
                            </div>
                            {suggestion.reasoning && (
                              <p className="mt-2 text-sm text-muted-foreground">{suggestion.reasoning}</p>
                            )}
                            <div className="mt-3 flex flex-wrap gap-2">
                              {suggestion.recommended_tests && (
                                <Badge variant="outline">{t("transcription.testsLabel", { tests: suggestion.recommended_tests })}</Badge>
                              )}
                              <Button
                                size="sm"
                                className="h-8 text-xs"
                                variant="secondary"
                                onClick={() => handleSuggestionApply(suggestion)}
                              >
                                {t("transcription.insertIntoForm")}
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Unknown Terms */}
                  {unknownTerms.length > 0 && (
                    <div className="rounded-md border bg-muted/40 p-3">
                      <p className="text-sm font-semibold text-foreground">{t("transcription.termsToReview")}</p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {unknownTerms.map((term) => (
                          <Badge
                            key={`${term.term}-${term.context}`}
                            variant="outline"
                            className="cursor-pointer"
                            onClick={() => handleUnknownTermNote(term)}
                          >
                            {term.term}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* History */}
                  <div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm font-semibold">
                        <HistoryIcon className="h-4 w-4" />
                        {t("transcription.recentHistory")}
                      </div>
                      <Button variant="ghost" size="sm" onClick={refreshHistory}>
                        <RefreshCcw className="mr-2 h-4 w-4" />
                        {t("transcription.refresh")}
                      </Button>
                    </div>
                    <div className="mt-3 space-y-2">
                      {isHistoryLoading && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          {t("transcription.loadingHistory")}
                        </div>
                      )}
                      {!isHistoryLoading && historyEmpty && (
                        <p className="text-sm text-muted-foreground">
                          {t("transcription.emptyHistory")}
                        </p>
                      )}
                      {!historyEmpty && (
                        <div className="space-y-2">
                          {history.map((entry) => (
                            <div
                              key={entry.id}
                              className={cn(
                                "rounded-md border p-3 text-sm transition hover:bg-muted/40",
                                selectedHistoryId === entry.id && "border-primary",
                              )}
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="font-medium">{formatTimestamp(entry.created_at) ?? t("transcription.noDate")}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {entry.summary ?? entry.transcript?.slice(0, 64) ?? t("transcription.noSummary")}
                                  </p>
                                </div>
                                <div className="flex flex-col gap-1 sm:flex-row">
                                  <Button
                                    size="sm"
                                    className="h-8 text-xs"
                                    variant="outline"
                                    onClick={() => {
                                      reviewHistoryEntry(entry);
                                    }}
                                  >
                                    {t("transcription.review")}
                                  </Button>
                                  <Button
                                    size="sm"
                                    className="h-8 text-xs"
                                    variant="secondary"
                                    disabled={isProcessing && selectedHistoryId === entry.id}
                                    onClick={() => {
                                      applyHistoryEntry(entry);
                                      toast.success(t("transcription.historyLoaded"));
                                    }}
                                  >
                                    {t("transcription.apply")}
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </TooltipProvider>
            </ScrollArea>

            <Separator />

            {/* Footer */}
          </aside>
        </div>
      )}

      {/* Floating Mini Player */}
      {floatingVisible && (
        <div className="fixed inset-x-4 bottom-[calc(84px+env(safe-area-inset-bottom))] z-50 hidden items-center gap-3 rounded-2xl border bg-background/95 px-4 py-2 shadow-2xl md:flex md:inset-x-auto md:bottom-6 md:right-6 md:rounded-full">
          <div className="flex items-center gap-2">
            {state === "ready" && recordingPhase === "idle" && recordingDuration === 0 ? (
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            ) : state === "interrupted" ? (
              <>
                <CloudOff className="h-4 w-4 text-destructive" />
                <span className="text-xs font-semibold text-foreground">
                  {t("transcription.connection.lostTitle")}
                </span>
              </>
            ) : isBusyProcessing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin text-primary" />
                <span className="text-xs font-semibold text-foreground">{statusLabel}</span>
              </>
            ) : state === "analysis_failed" ? (
              <>
                <TriangleAlert className="h-4 w-4 text-destructive" />
                <span className="text-xs font-semibold text-foreground">{t("transcription.aiFailure.title")}</span>
              </>
            ) : (
              <>
                <span
                  className={cn(
                    "inline-flex h-2 w-2 rounded-full",
                    recordingPhase === "recording"
                      ? "bg-red-500 animate-pulse"
                      : recordingPhase === "paused"
                        ? "bg-amber-500"
                        : "bg-muted-foreground/60",
                  )}
                />
                <span className="text-xs font-semibold text-foreground">{formatDuration(recordingDuration)}</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-1">
            {state === "interrupted" && (
              <Button
                variant="ghost"
                size="icon"
                onClick={reconnect}
                disabled={isReconnecting}
                aria-label={t("transcription.connection.reconnect")}
              >
                <RefreshCcw className={cn("h-4 w-4", isReconnecting && "animate-spin")} />
              </Button>
            )}
            {state !== "interrupted" && (recordingPhase === "recording" || recordingPhase === "paused") && (
              <>
                {recordingPhase === "recording" ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={pauseRecording}
                    disabled={isFinalizing}
                    aria-label={t("transcription.pauseRecording")}
                  >
                    <Pause className="h-4 w-4" />
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={resumeRecording}
                    disabled={isFinalizing || isStarting}
                    aria-label={t("transcription.resumeRecording")}
                  >
                    <Play className="h-4 w-4" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={stopRecording}
                  disabled={isFinalizing}
                  aria-label={t("transcription.stopRecording")}
                >
                  <Square className="h-4 w-4" />
                </Button>
              </>
            )}
            {state === "analysis_failed" && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => void retryAiExtraction()}
                aria-label={t("mobile.recordingBar.retry")}
              >
                <RefreshCcw className="h-4 w-4" />
              </Button>
            )}
            <Button variant="ghost" size="icon" onClick={maximize} aria-label={t("transcription.maximize")}>
              <Maximize2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Cancel Recording Dialog */}
      <AlertDialog
        open={isCancelRecordingDialogOpen}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            setIsCancelRecordingDialogOpen(false);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("transcription.cancelRecordingTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("transcription.cancelRecordingDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setIsCancelRecordingDialogOpen(false)}>
              {t("transcription.continueRecording")}
            </AlertDialogCancel>
            <AlertDialogAction onClick={discardSession}>{t("transcription.cancelRecording")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
